/**
 * sse_client.js — shared SocketIO consumer (plumbing only)
 *
 * Purpose
 * -------
 * Single shared SocketIO consumer that listens for ticket lifecycle events
 * (pin / unpin / subscribe / unsubscribe / archive) and re-broadcasts them
 * as DOM CustomEvents on `window`. Templates that want to react to these
 * events subscribe to the `sse:*` CustomEvents — they do NOT touch the
 * socket directly. Per-template consumers ship in later waves; this slice
 * is only the plumbing.
 *
 * Load order
 * ----------
 * `socket.io.js` (the client library, ~52 KB CDN) is loaded earlier in the
 * `{% block socketio_js %}` opt-in block. This file MUST load after it so
 * the `io` global is defined.
 *
 * Disable per page
 * ----------------
 * Set `window.__SSE_ENABLED__ = false` before this script runs to no-op.
 *
 * Auto-reconnect
 * --------------
 * Socket.IO client handles reconnection by default (exponential backoff,
 * `reconnection: true`). No manual reconnect logic required.
 *
 * CSP
 * ---
 * Pure static file (no inline). Loaded via `<script src=... defer>` in
 * base.html. No CSP nonce needed for external scripts in this project.
 */

(function () {
  "use strict";

  // CustomEvent names dispatched on `window`. Templates listen via
  //   window.addEventListener('sse:ticket.pinned', (ev) => { ... ev.detail ... });
  // Payload is passed through unmodified as `ev.detail`.
  var SSE_EVENT_MAP = {
    "ticket.pinned": "sse:ticket.pinned",
    "ticket.unpinned": "sse:ticket.unpinned",
    "ticket.subscribed": "sse:ticket.subscribed",
    "ticket.unsubscribed": "sse:ticket.unsubscribed",
    "ticket.archived": "sse:ticket.archived",
    "ticket.moved": "sse:ticket.moved",
    // MPBL-385 — auto-update the kanban + story detail when other
    // users or MCP-driven agents mutate tickets / comments. These
    // events already fire on the backend (`_emit_event` in
    // app/routes/desktop_api.py) — they were just missing from the
    // client-side allowlist so the consumer never got them.
    "ticket.created": "sse:ticket.created",
    "ticket.updated": "sse:ticket.updated",
    "comment.created": "sse:comment.created",
    "subtasks.bulk_created": "sse:subtasks.bulk_created",
    "comments.bulk_created": "sse:comments.bulk_created",
    // MPBL-446 — live Jira-import progress for the admin import wizard.
    // The background worker (jira_import_service.emit_import_progress) emits
    // this to the importing user's org room; the wizard listens and updates
    // its progress bar / log without polling. Polling stays as fallback.
    "jira_import.progress": "sse:jira_import.progress",
  };

  function dispatchSse(eventName, payload) {
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    } catch (err) {
      // Never let a downstream listener exception break socket plumbing.
      // CustomEvent constructor itself cannot throw on modern browsers, but
      // listener side-effects can — addEventListener swallows those, so the
      // only failure path here is older browsers without CustomEvent.
      if (window.console && console.warn) {
        console.warn("[sse_client] dispatch failed for", eventName, err);
      }
    }
  }

  function bootSseClient() {
    // MPBL-385 — unconditional trace BEFORE any bailout so we can tell
    // from one console line which of the three failure modes hit:
    //   `sse_client.js loaded, io=function, SSE=true`  → wire will boot
    //   `sse_client.js loaded, io=undefined, SSE=true` → CDN didn't load
    //   `sse_client.js loaded, io=function, SSE=false` → template opted out
    // eslint-disable-next-line no-console
    console.log(
      "[sse_client] loaded, io=" +
        typeof window.io +
        ", SSE=" +
        (window.__SSE_ENABLED__ !== false),
    );

    // Per-template opt-out: a template that explicitly does NOT want the
    // shared consumer can set `window.__SSE_ENABLED__ = false` in a small
    // inline script before this file loads.
    if (window.__SSE_ENABLED__ === false) {
      return;
    }

    // `io` must be defined by socket.io.js earlier in the opt-in block.
    // Depending on the build, the global may surface as a callable
    // function (UMD/IIFE) OR an object whose `.io` (Manager factory)
    // is callable (ESM-style v4 build). Accept either; bail when
    // neither is usable.
    var ioGlobal = window.io;
    if (!ioGlobal) return;
    var ioFn =
      typeof ioGlobal === "function"
        ? ioGlobal
        : typeof ioGlobal.connect === "function"
          ? ioGlobal.connect
          : typeof ioGlobal.io === "function"
            ? ioGlobal.io
            : null;
    if (!ioFn) {
      var keys = Object.keys(ioGlobal || {});
      var keyDump = JSON.stringify(keys.slice(0, 20));
      var ctorNames = keys
        .slice(0, 20)
        .map(function (k) {
          var v = ioGlobal[k];
          var ctor = (v && v.constructor && v.constructor.name) || typeof v;
          var preview = "";
          try {
            preview = String(v).slice(0, 60);
          } catch (e) {}
          return k + ":" + ctor + "(" + preview + ")";
        })
        .join(" | ");
      // eslint-disable-next-line no-console
      console.warn(
        "[sse_client] window.io is " +
          typeof ioGlobal +
          " isArray=" +
          Array.isArray(ioGlobal) +
          " length=" +
          (ioGlobal && ioGlobal.length) +
          " ctor=" +
          (ioGlobal && ioGlobal.constructor && ioGlobal.constructor.name) +
          " keys=" +
          keyDump +
          " entries=[" +
          ctorNames +
          "]",
      );
      // Last-ditch fallback: search the entries for a function we can
      // call. Some extensions wrap `io` as [Manager, Socket, fn] or
      // similar — sniffing the array protects against that without
      // hard-coding any one extension's shape.
      if (Array.isArray(ioGlobal)) {
        for (var i = 0; i < ioGlobal.length; i++) {
          if (typeof ioGlobal[i] === "function") {
            console.log("[sse_client] using array entry", i, "as io()");
            ioFn = ioGlobal[i];
            break;
          }
        }
      }
      if (!ioFn) return;
    }

    // Default namespace + default URL = backend root (same origin).
    // Socket.IO defaults: reconnection enabled, exponential backoff, infinite
    // attempts. No options needed.
    var socket;
    try {
      socket = ioFn();
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("[sse_client] io() failed", err);
      }
      return;
    }

    // MPBL-385 — verbose logging while we stabilise the multi-tab
    // auto-update path. Each lifecycle event prints to the console so a
    // dev can see at a glance whether the wire is connected and which
    // server events are arriving. Remove these `console.log` lines
    // once the path is proven in prod.
    socket.on("connect", function () {
      // eslint-disable-next-line no-console
      console.log("[sse_client] socket connected, sid=", socket.id);
    });
    socket.on("disconnect", function (reason) {
      // eslint-disable-next-line no-console
      console.log("[sse_client] socket disconnected:", reason);
    });
    socket.on("connect_error", function (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[sse_client] connect_error:",
        err && err.message ? err.message : err,
      );
    });

    // Register one listener per known event. The server payload is passed
    // through unmodified into `ev.detail`.
    Object.keys(SSE_EVENT_MAP).forEach(function (serverEvent) {
      var clientEvent = SSE_EVENT_MAP[serverEvent];
      socket.on(serverEvent, function (payload) {
        // eslint-disable-next-line no-console
        console.log("[sse_client] recv", serverEvent, payload);
        dispatchSse(clientEvent, payload);
      });
    });

    // Expose the socket for debugging only. Production templates MUST use
    // the CustomEvent API — direct socket access is intentionally not part
    // of the contract.
    window.__sseSocket = socket;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootSseClient, {
      once: true,
    });
  } else {
    // `defer` script runs after DOM parse so readyState is already
    // 'interactive' or 'complete' — boot immediately.
    bootSseClient();
  }
})();
